import { ApiService } from './api';
import type { TTSVoice, TTSSynthesizePayload } from '../types/voice';

const SELECTED_VOICE_KEY = 'tts-voice-code';
const LEGACY_SELECTED_VOICE_KEY = 'tts_voice_code';

export class TTSService {
  private static currentAudio: HTMLAudioElement | null = null;
  private static getAudioElement(): HTMLAudioElement {
    let el = document.getElementById('kiosk-tts-audio') as HTMLAudioElement | null
    if (!el) {
      el = document.createElement('audio')
      el.id = 'kiosk-tts-audio'
      el.style.position = 'fixed'
      el.style.left = '-9999px'
      el.style.width = '0'
      el.style.height = '0'
      el.preload = 'auto'
      el.setAttribute('playsinline', '')
      el.muted = false
      document.body.appendChild(el)
      try { console.info('TTS: created hidden audio element') } catch {}
    }
    try { console.debug('TTS: using audio element', { readyState: el.readyState, paused: el.paused, muted: el.muted }) } catch {}
    return el
  }
  static getSelectedVoiceCode(): string | null {
    const v = localStorage.getItem(SELECTED_VOICE_KEY);
    if (v) return v;
    const legacy = localStorage.getItem(LEGACY_SELECTED_VOICE_KEY);
    if (legacy) {
      try { localStorage.setItem(SELECTED_VOICE_KEY, legacy); } catch {}
      return legacy;
    }
    return null;
  }

  static setSelectedVoiceCode(code: string): void {
    localStorage.setItem(SELECTED_VOICE_KEY, code);
  }

  static async listVoices(params?: {
    voiceOwnership?: string;
    languageCode?: string;
    gender?: string;
    limit?: number;
    cursor?: string;
    code?: string;
  }): Promise<TTSVoice[]> {
    const qp = new URLSearchParams();
    if (params?.voiceOwnership) qp.set('voiceOwnership', params.voiceOwnership);
    if (params?.languageCode) qp.set('languageCode', params.languageCode);
    if (params?.gender) qp.set('gender', params.gender);
    if (params?.limit) qp.set('limit', String(params.limit));
    if (params?.cursor) qp.set('cursor', params.cursor);
    if (params?.code) qp.set('code', params.code);

    const res = await ApiService.get(`/api/tts/voices?${qp.toString()}`);
    return res.data?.voices || [];
  }

  static async speakText(payload: TTSSynthesizePayload): Promise<{ requestId?: string; audioLink?: string }> {
    const res = await ApiService.postBinaryOrJson('/api/tts/synthesize', payload);
    // Backward-compatible: audio stream or audio_url
    if (res instanceof Blob) {
      await this.playAudioFromBlob(res);
      return {};
    }
    if (res && (res as any).audio_url) {
      const audioUrl = (res as any).audio_url as string;
      await this.playAudioFromUrl(audioUrl);
      return { audioLink: audioUrl };
    }
    // Async flow: expect request_id then poll status
    if (res && (res as any).data?.request_id) {
      const requestId = (res as any).data.request_id as string;
      const statusRes = await this.pollTTSStatus(requestId, { timeoutMs: 30000, intervalMs: 1000 });
      if (statusRes.status === 'SUCCESS' && statusRes.audio_link) {
        await this.playAudioFromUrl(statusRes.audio_link);
        return { requestId, audioLink: statusRes.audio_link };
      }
      const fallback = await this.getRequest(requestId);
      if (fallback && fallback.status === 1 && fallback.result?.audio_link) {
        const audioUrl = fallback.result.audio_link as string;
        await this.playAudioFromUrl(audioUrl);
        return { requestId, audioLink: audioUrl };
      }
      const errMsg = statusRes.error_message || fallback?.error_message || 'TTS request failed or timed out';
      throw new Error(errMsg);
    }
    throw new Error('Unexpected TTS response');
  }

  static async playAudioFromBlob(blob: Blob, opts?: { allowMutedFallback?: boolean }): Promise<void> {
    try { console.info('TTS: play from Blob', { size: blob.size, type: blob.type, allowMutedFallback: !!opts?.allowMutedFallback }) } catch {}
    const url = URL.createObjectURL(blob);
    try {
      await this.playAudioFromUrl(url, opts);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  static async playAudioFromUrl(url: string, opts?: { allowMutedFallback?: boolean }): Promise<void> {
    try { console.info('TTS: play from URL', { url, allowMutedFallback: !!opts?.allowMutedFallback }) } catch {}
    try {
      this.stopCurrent();
    } catch {}
    const audio = this.getAudioElement();
    audio.src = url;
    audio.currentTime = 0;
    this.currentAudio = audio;
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        if (this.currentAudio === audio) this.currentAudio = null;
        audio.onended = null as any;
        audio.onpause = null as any;
        audio.onerror = null as any;
        audio.onabort = null as any;
        audio.onstalled = null as any;
      };
      const addUnmuteOnInteract = () => {
        const unmute = () => {
          try { audio.muted = false; console.info('TTS: unmuted on user interaction') } catch {}
          document.removeEventListener('click', unmute);
          document.removeEventListener('touchstart', unmute);
          document.removeEventListener('keydown', unmute);
        };
        document.addEventListener('click', unmute, { once: true });
        document.addEventListener('touchstart', unmute, { once: true });
        document.addEventListener('keydown', unmute, { once: true });
      };
      audio.onended = () => { try { console.info('TTS: audio ended') } catch {}; cleanup(); resolve(); };
      audio.onpause = () => { try { console.info('TTS: audio paused') } catch {}; cleanup(); resolve(); };
      audio.onerror = () => { try { console.error('TTS: audio error') } catch {}; cleanup(); reject(new Error('Audio playback error')); };
      audio.onabort = () => { try { console.info('TTS: audio aborted') } catch {}; cleanup(); resolve(); };
      audio.onstalled = () => { try { console.warn('TTS: audio stalled') } catch {}; cleanup(); reject(new Error('Audio playback stalled')); };
      const playPromise = audio.play()
      playPromise.then(() => { try { console.info('TTS: audio play() resolved', { muted: audio.muted }) } catch {} }).catch(err => {
        const name = (err && (err as any).name) || '';
        if (name === 'NotAllowedError' && opts?.allowMutedFallback) {
          try {
            audio.muted = true;
            audio.play().then(() => {
              try { console.warn('TTS: autoplay blocked, playing muted and waiting for interaction') } catch {}
              addUnmuteOnInteract();
            }).catch(e => {
              const retryOnInteract = () => {
                document.removeEventListener('click', retryOnInteract);
                document.removeEventListener('touchstart', retryOnInteract);
                document.removeEventListener('keydown', retryOnInteract);
                audio.muted = false;
                audio.play().then(() => { try { console.info('TTS: resumed play after interaction') } catch {} }).catch(err2 => { try { console.error('TTS: play failed after interaction', err2) } catch {}; cleanup(); reject(err2); });
              };
              document.addEventListener('click', retryOnInteract, { once: true });
              document.addEventListener('touchstart', retryOnInteract, { once: true });
              document.addEventListener('keydown', retryOnInteract, { once: true });
              try { console.warn('TTS: muted play failed, will retry on interaction', e) } catch {}
            });
          } catch (e2) {
            try { console.error('TTS: muted fallback threw', e2) } catch {}
            cleanup();
            reject(e2 as any);
          }
        } else if (name === 'NotAllowedError') {
          try { console.warn('TTS: autoplay blocked and muted fallback disabled') } catch {}
          cleanup();
          reject(err);
        } else {
          try { console.error('TTS: audio.play() failed', err) } catch {}
          cleanup();
          reject(err);
        }
      });
    });
  }

  static stopCurrent(): void {
    try {
      if (this.currentAudio) {
        this.currentAudio.pause();
        try { console.info('TTS: stopped current audio') } catch {}
        this.currentAudio = null;
      }
    } catch {}
  }

  static async pollTTSStatus(requestId: string, opts?: { timeoutMs?: number; intervalMs?: number }): Promise<{ status: 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE'; audio_link?: string; error_code?: string; error_message?: string }>{
    const timeoutMs = opts?.timeoutMs ?? 60000;
    const intervalMs = opts?.intervalMs ?? 1000;
    const start = Date.now();
    while (true) {
      const res = await ApiService.get(`/api/tts/status?request_id=${encodeURIComponent(requestId)}&long_poll=true&timeout_ms=${timeoutMs}&interval_ms=${intervalMs}`);
      const data = (res?.data ?? res) as any;
      // Try common wrappers
      const status = (data?.status) || (res?.status);
      if (status && status !== 'IN_PROGRESS') {
        return {
          status,
          audio_link: data?.audio_link,
          error_code: data?.error_code,
          error_message: data?.error_message
        };
      }
      if (Date.now() - start >= timeoutMs) {
        return {
          status: 'IN_PROGRESS',
          error_message: 'Quá thời gian chờ kết quả'
        } as any;
      }
      await new Promise(r => setTimeout(r, intervalMs));
    }
  }

  static async getRequest(requestId: string): Promise<{ status: number; error_code?: string; error_message?: string; result?: Record<string, any> }>{
    const res = await ApiService.get(`/api/tts/request?request_id=${encodeURIComponent(requestId)}`);
    return (res?.data ?? res) as any;
  }
}
